import type { ItemChange, ListChange } from "../data/store.js";
import type { Item, List, ListKind } from "../lib/api.js";
import { el, moveKeepingFocus } from "../lib/dom.js";
import { todayIso } from "../lib/format.js";
import { icon } from "../lib/icons.js";
import { hasNote, paintNotePreview } from "../lib/markdown.js";
import { dueOrder, inToday, inUpcoming, listOrder, project } from "../lib/projection.js";
import {
  back,
  agendaItemPath,
  agendaPath,
  listPath,
  navigate,
  type Agenda,
  type Route,
} from "../lib/router.js";
import {
  allDoneMessage,
  completedControl,
  completedVisibility,
  hiddenCountMessage,
  revealedMessage,
  SHOWING_COMPLETED,
  tickedHiddenMessage,
  toggleCompleted,
} from "./completed.js";
import { editorPanel } from "./editor.js";
import { openActivity } from "./activity.js";
import { openNote } from "./noteViewer.js";
import { openShareItem } from "./share.js";
import { SEPARATOR, actionMenu, actionsButton, type MenuItem } from "./menu.js";
import {
  DELETE_ITEM,
  KIND_ICON,
  ROW_GONE,
  keepFocusIn,
  KIND_LABEL,
  banner,
  deleteTitle,
  editorGroup,
  inlineText,
  messageOf,
  noteOpener,
  paintMeta,
  rowPresentation,
  unreachable,
  type Screen,
  type Shell,
} from "./ui.js";

/**
 * Today and Upcoming: the two grouped projections, read from the same mirror
 * every other screen reads.
 *
 * One module for both because they differ in four things — which items belong,
 * how a group's rows are read (g91), what the heading says, and what an empty
 * one says — and in nothing else. The
 * grouping itself is in `lib/projection.ts`, which is pure, so what belongs in
 * Today is decided by a function that needs no database and no clock.
 *
 * What a row may do here is narrower than in a list, deliberately. The checkbox
 * is live, because ticking something off from Today is the point of Today, and
 * so is the panel. There is no insert-below and no reorder: a position here is
 * named by neighbours that belong to *different lists*, so an anchor would name
 * no unambiguous place, and that is exactly where filtered-anchor bugs live.
 * Upcoming's date order (g91) is a way of reading and gives nothing to drag.
 */

/**
 * How many rows are drawn before the rest go behind a control.
 *
 * Rows rather than groups: the case this exists for is one list holding
 * thousands of dated items, which a group count does nothing about. Generous
 * enough that an ordinary account never meets it — about thirteen phone screens
 * — and small enough that the worst case is trivial DOM.
 */
const BUDGET = 200;

const TITLE: Record<Agenda, string> = { today: "Today", upcoming: "Upcoming" };

/**
 * State-specific, with no fallback content. An empty Today is information;
 * filling it with undated items would make Today mean two things.
 */
// Each says its rule (g56), checked against `projection.ts`: Today holds what
// is due today *and* anything unticked from before, so it says "overdue" too;
// Upcoming holds anything dated after today.
export const NOTHING: Record<Agenda, string> = {
  today: "Nothing due today or overdue. Items appear here on their due date and stay while overdue.",
  upcoming: "Nothing due after today. Items with a later due date appear here.",
};

interface Row {
  node: HTMLElement;
  update(item: Item): void;
  /** What the row is currently showing — the reconciler compares against it. */
  shown(): Item;
  /** True while the row's inline editor holds typed changes. */
  dirty(): boolean;
  /** The item is gone and the row stays for its draft: as the list screen's (g54). */
  orphan(onDiscard: () => void): void;
}

interface GroupNode {
  node: HTMLElement;
  rows: HTMLElement;
  name: HTMLElement;
  link: HTMLAnchorElement;
  /** The kind's glyph, leading the link; replaced, never re-queried, when the kind moves. */
  glyph: SVGSVGElement;
  /** The kind's word, present only while the kind is not the default. */
  word: HTMLElement | undefined;
  /** Trails the name; the word goes in before it. */
  chevron: SVGSVGElement;
  /** What it was last painted with, so a change can tell the kind moved. */
  kind: ListKind;
}

/**
 * The link's accessible name: "Groceries, Shopping", or just the name for the
 * default kind — `listLabel`'s rule without its count, since a projection's
 * heading has no badge. Said explicitly because the visible words are two
 * spans, and a name computed from them runs "GroceriesShopping" together.
 */
function groupLabel(list: Pick<List, "name" | "kind">): string {
  return list.kind === "todo" ? list.name : `${list.name}, ${KIND_LABEL[list.kind]}`;
}

export function agendaScreen(shell: Shell, which: Agenda): Screen {
  const { store } = shell;
  const member = (item: Item, today: string): boolean =>
    which === "today" ? inToday(item, today) : inUpcoming(item, today);
  // How a group's rows are read (g91): Today as each list has them, Upcoming
  // nearest due date first. The one comparator the build and every placement
  // below share, so a row can never be drawn where a rebuild would not put it.
  const order = which === "today" ? listOrder : dueOrder;

  const heading = el("h1", { text: TITLE[which] });
  const status = el("div", { class: "status" });
  /** The deep-link explanation. Not `status`: every write that takes clears that. */
  const notice = el("div", { class: "status agenda-notice" });
  const groupsNode = el("div", { class: "groups" });
  // `status` for the `:empty` rule, and a second class because this is not the
  // same slot as the one above it: that one speaks about a write that failed,
  // this one about why there is nothing on the screen.
  const state = el("div", { class: "status agenda-state" });
  /** The last thing `paintState` announced; see it for why. */
  let said = "";
  const moreSlot = el("div", { class: "agenda-more" });

  // Whether this projection shows its completed rows: a device preference of
  // this account's and this view's, apart from every list's and from the
  // other projection's (C05). Named `completed` and not `hidden`, which is
  // the budget's set below.
  const completed = completedVisibility({ userId: shell.user.id, view: which });
  const control = completedControl(() =>
    toggleCompleted({ editors, detail: shell.detail, selectedWouldHide, dismiss, apply }),
  );
  // The one heading and the control beside it: the control is a sibling, so
  // the page still has exactly one `h1` and it says only the title.
  const head = el("div", { class: "agenda-head", children: [heading, control.node] });

  // The row's actions menu (g18): one for the screen, opened beside whichever
  // ellipsis was pressed, and appended to the screen rather than to a row —
  // see `views/menu.ts` for why.
  const menu = actionMenu();
  const node = el("section", {
    class: "screen agenda",
    children: [head, status, notice, state, groupsNode, moreSlot, menu.node],
  });

  const setStatus = (child: Node | undefined) => {
    status.replaceChildren(...(child ? [child] : []));
  };
  /**
   * Shown in the slot, and said once in the shell's live region.
   *
   * Both, because they answer different people: the slot is what a sighted
   * person reads, and the region is the only thing a screen reader hears. The
   * slot cannot do the second job — it is hidden while empty, so it is outside
   * the accessibility tree exactly when a live region needs to be inside it.
   */
  const fail = (error: unknown) => {
    const message = messageOf(error);
    setStatus(banner(message));
    shell.announce(message);
  };
  /**
   * A row this change moved to another place in its group because its date or
   * time changed (g91), worded for the live region. Set by `item` and cleared
   * at the start of every call to it, so it only ever describes the change
   * that has just arrived.
   */
  let movedNote: string | undefined;
  /**
   * The panel's save took, or did not. The store's change event arrives inside
   * the write, before this, so a note still standing here is about this
   * device's own save: that move is said. One arriving from another device is
   * drawn the same way and not said, as another device's tick is not.
   */
  const onResult = (error?: unknown) => {
    if (error !== undefined) return fail(error);
    setStatus(undefined);
    if (movedNote !== undefined) shell.announce(movedNote);
    movedNote = undefined;
  };

  const groups = new Map<string, GroupNode>();
  const nodes = new Map<string, Row>();
  /**
   * Members the last build decided not to draw.
   *
   * Without it a change arriving for one of them would look exactly like a new
   * item and get drawn, so the budget would leak one row at a time.
   */
  let hidden = new Set<string>();
  // The boundary the rows on screen were built against, so a rebuild can tell
  // it has crossed midnight and a row can be painted against the same day it
  // was admitted under.
  let today = todayIso();
  const isDone = (item: Item | undefined): boolean => Boolean(item &&
    (item.done || (which === "today" && item.repeat && item.lastDoneOn === today)));
  // Asked for by the person, so it outlives a rebuild — switching to another
  // app and back must not collapse what they opened. It does not outlive the
  // screen: a fresh mount starts at the budget again.
  let expanded = false;

  let selectedId: string | undefined;
  let shownId: string | undefined;
  let pushed = false;
  // One open editor at a time here too — the same contract as a list's rows,
  // from the same helper, rather than this screen's own loop over every row.
  const editors = editorGroup();
  // A completed row a deep link asked for while the filter hides it (C05):
  // drawn for this visit, the preference left alone. See `list.ts`.
  let revealed: string | undefined;
  // The row being ticked right now, so `item` can tell this device's tick
  // from another's — the first is said, the second is not.
  let ticking: string | undefined;
  // The row this screen is deleting from its menu — see the same marker on
  // the list screen: its own delete asks nothing more (D09).
  let deleting: string | undefined;

  // ---- completed-item visibility (C01-C05) ----------------------------------

  /** Completed members of the projection, drawn or behind Show more alike. */
  function doneCount(): number {
    return store.allItems().filter((item) => isDone(item) && member(item, today)).length;
  }

  /**
   * A done row the filter leaves alone: the one the panel shows (its panel
   * comes down first, and the row goes in the sweep that follows), one whose
   * inline editor holds typed changes (until they are saved or dropped), and
   * the one a deep link revealed. The same rule as the list's.
   */
  function sticky(id: string): boolean {
    return shownId === id || revealed === id || (nodes.get(id)?.dirty() ?? false);
  }

  /** C04 in hide mode: a done row nothing is keeping on screen. */
  function filtered(item: Item): boolean {
    return completed.hidden && isDone(item) && !sticky(item.id);
  }

  /**
   * Takes drawn rows off, and moves the focus on if it was in one: the next
   * visible checkbox on the page, then the previous, then the control (C04).
   *
   * In document order across the groups, not among one group's siblings: the
   * rows here are split over one `ul` per list, and the checkbox after the
   * last row of a group is the first row of the next one. Read before
   * anything is dropped, skipping rows leaving in the same pass.
   */
  function dropRows(ids: readonly string[]): void {
    const going = new Set(ids);
    const drawn = [...groupsNode.querySelectorAll<HTMLElement>(".items .item")];
    const at = drawn.findIndex((row) => row.contains(document.activeElement));
    let landing: HTMLElement | undefined;
    if (at >= 0) {
      const staying = (row: HTMLElement) => !going.has(row.dataset["id"] ?? "");
      const next = drawn.slice(at + 1).find(staying);
      const previous = drawn.slice(0, at).reverse().find(staying);
      // The same control on the row that stays: the ellipsis when the focus
      // was on the ellipsis (D09), else the checkbox (C04) -- `drop` lands
      // the ellipsis case itself, and this must not undo it.
      const same = document.activeElement?.closest(".item-actions") ? ".item-actions" : ".check input";
      // Rows leave here only while the filter is on and hiding a completed
      // row, and then the control is offered (g52, g91), so this is on screen.
      landing = (next ?? previous)?.querySelector<HTMLElement>(same) ?? control.node;
    }
    for (const id of ids) drop(id);
    landing?.focus();
  }

  /** Every drawn row the filter no longer admits goes. */
  function sweep(): void {
    const gone = [...nodes].filter(([, row]) => filtered(row.shown())).map(([id]) => id);
    if (gone.length) dropRows(gone);
    paintState();
  }

  function paintControl(): void {
    control.paint(completed.hidden, doneCount(), completed.chosen);
  }

  function clearNotice(): void {
    revealed = undefined;
    notice.replaceChildren();
  }

  /** Whether the row the panel shows would leave once the filter goes on. */
  function selectedWouldHide(): boolean {
    return shownId !== undefined && !completed.hidden && isDone(store.item(shownId));
  }

  /**
   * The preference flips and the projection is rebuilt against it. Nothing
   * is written to the store. Reached only once drafts are settled — see
   * `toggleCompleted` — so the rebuild costs nothing.
   */
  function apply(): void {
    clearNotice();
    completed.set(!completed.hidden);
    // Said before the rebuild: an all-done projection says so from
    // `paintState`, and that line — which carries the count — is the one
    // to leave standing.
    shell.announce(completed.hidden ? hiddenCountMessage(doneCount()) : SHOWING_COMPLETED);
    build();
    // A rebuild may put the panel's row back behind Show more — one a link
    // drew on demand, or a done row the budget now reaches past — leaving an
    // open form with no row to return the focus to. Drawn again the way the
    // link drew it; anything else the rebuild dropped goes through the guard.
    if (shownId !== undefined && !nodes.has(shownId) && hidden.has(shownId)) {
      const item = store.item(shownId);
      if (item) place(item, itemRow(item));
      if (nodes.has(shownId)) {
        hidden.delete(shownId);
        paintMore();
      }
    }
    reconcile();
    paintControl();
  }

  // ---- rows ---------------------------------------------------------------

  function itemRow(item: Item): Row {
    let shown = item;

    const check = el("input", { attrs: { type: "checkbox", checked: item.done } });
    const itemText = inlineText({
      text: item.text,
      // See the same label on the list screen (g58).
      label: (text, quantity) => (quantity ? `Edit ${quantity} ${text}` : `Edit ${text}`),
      commit(next) {
        try {
          store.patchItem(shown.id, { text: next });
          setStatus(undefined);
          return true;
        } catch (error: unknown) {
          fail(error);
          return false;
        }
      },
      onToggle: (open) => {
        // See the same handler on the list screen.
        if (open && selectedId === shown.id) dismiss();
        paint(shown);
        // The draft that was keeping a done row on screen is saved or
        // dropped: the row goes now (C04), and the focus rule moves on.
        if (!open && filtered(shown)) {
          dropRows([shown.id]);
          paintState();
        }
      },
      announce: (message) => shell.announce(message),
      group: editors,
    });
    const meta = el("span", { class: "item-meta" });
    const note = el("span", { class: "item-note" });
    // The note page (g77), from View note in the menu or a tap on the
    // preview (g139). Whichever row is editing is resolved first, as Edit
    // does; a changed draft in the inspector is asked about by the departure
    // guard, since the page replaces this screen.
    const viewNote = () => { if (editors.resolve()) openNote(shown.listId, shown.id); };
    noteOpener(note, () => { if (hasNote(shown.note)) viewNote(); });

    // The ellipsis opens the actions menu (g18): Open list, Edit, Delete. No
    // move and no insert, which is the settled rule rather than an omission:
    // an anchor named by neighbours from *different* lists names no
    // unambiguous place. See the note at the top of this file.
    const actions = actionsButton();
    actions.addEventListener("click", () => menu.open(actions, entries));
    const entries = (): MenuItem[] => [
      {
        label: "Open list",
        icon: "list",
        // Whichever row is editing is resolved first, as Edit does: the
        // ellipsis is there while a draft is being typed, and a navigation
        // that unmounted the screen would take the draft with it; a refusal
        // keeps the draft and goes nowhere. Then through the guard (E05): a
        // changed draft in the inspector is asked about, not thrown away. A
        // push, not a replace: Back from the list returns here.
        onSelect: () => {
          if (!editors.resolve()) return;
          shell.detail.guarded(() => navigate(listPath(shown.listId)));
        },
      },
      SEPARATOR,
      // Only with a note to read (g77).
      ...(hasNote(shown.note) ? [{
        label: "View note",
        icon: "note" as const,
        onSelect: viewNote,
      }] : []),
      // Who changed this item, and when (g110), while the server lets history be read.
      ...(store.activityAvailable() ? [{
        label: "Activity",
        icon: "history" as const,
        onSelect: () => { if (editors.resolve()) openActivity(shown.listId, shown.id); },
      }] : []),
      {
        label: "Edit",
        icon: "edit",
        onSelect: () => {
          // See the same handler on the list screen: whichever row is editing
          // is resolved first, and a refusal must not cost the draft.
          if (editors.resolve()) selectItem(shown.id);
        },
      },
      SEPARATOR,
      { label: "Share item", icon: "share", onSelect: () => {
        if (editors.resolve()) openShareItem(shell, shown.listId, shown.id, actions);
      } },
      SEPARATOR,
      { label: "Delete", icon: "trash", danger: true, onSelect: () => askDelete(shown.id) },
    ];

    const row = el("li", {
      attrs: { "data-id": item.id },
      children: [
        el("label", { class: "check", children: [check] }),
        el("div", { class: "item-body", children: [itemText.node, meta, note] }),
        actions,
      ],
    });

    const paint = (next: Item) => {
      shown = next;

      const look = rowPresentation({ ...next, done: isDone(next) }, {
        // The row's list as the store has it now: a Shopping item here leads
        // with its quantity as it does on its own list (g58).
        kind: store.list(next.listId)?.kind,
        today,
        selected: next.id === selectedId,
        editing: itemText.editing(),
      });
      row.className = look.className;

      check.checked = isDone(next);
      check.disabled = Boolean(next.repeat && (isDone(next) ? !next.completion : next.due && next.due.date > today));
      check.setAttribute(
        "aria-label",
        isDone(next) ? `Mark ${next.text} not done` : `Mark ${next.text} done`,
      );
      actions.setAttribute("aria-label", `Actions for ${next.text}`);

      // Writes the button, never an open field — see `show` in `inlineText`.
      itemText.show(next.text, look.prefix);
      paintMeta(meta, look.metaParts);
      // The note without its markdown (g76): a row shows words, never markup.
      paintNotePreview(note, next.note);
    };

    check.addEventListener("change", () => {
      // Nothing waits for the server and nothing rebuilds the projection, so
      // the control under the finger survives being used — and the row stays
      // where it is even when the tick takes it out of Today. See `item`.
      ticking = shown.id;
      try {
        if (shown.repeat) {
          if (check.checked) store.completeItem(shown.id);
          else store.undoCompletion(shown.id);
        } else store.patchItem(shown.id, { done: check.checked });
        setStatus(undefined);
      } catch (error: unknown) {
        check.checked = isDone(shown);
        fail(error);
      } finally {
        ticking = undefined;
      }
    });

    paint(item);
    return {
      node: row,
      update: paint,
      shown: () => shown,
      dirty: () => itemText.dirty(),
      orphan(onDiscard) {
        delete row.dataset["id"];
        row.classList.add("gone");
        check.disabled = true;
        actions.hidden = true;
        itemText.orphan(ROW_GONE, onDiscard);
      },
    };
  }

  // ---- groups -------------------------------------------------------------

  /**
   * A quiet heading that is a link (A02): the kind's glyph, the list's name,
   * the kind's word when it is not the default, and a chevron — all inside
   * the one `<a>`, so the whole line is the 44px target and the chevron is
   * what says the name goes somewhere. The same vocabulary as the Lists
   * overview's rows, which is where "what kind of list" was settled.
   */
  function groupNode(list: List): GroupNode {
    const name = el("span", { class: "group-name", text: list.name });
    const glyph = icon(KIND_ICON[list.kind]);
    const chevron = icon("chevron");
    const link = el("a", {
      class: "group-link",
      attrs: { href: listPath(list.id), "data-link": true },
      children: [glyph, name, chevron],
    });
    const head = el("h2", { class: "group-head", children: [link] });
    const rows = el("ul", { class: "items" });
    return {
      node: el("section", { class: "group", attrs: { "data-id": list.id }, children: [head, rows] }),
      rows,
      name,
      link,
      glyph,
      word: undefined,
      chevron,
      kind: list.kind,
    };
  }

  /**
   * The name, the glyph, and — off the default kind — the word that explains
   * why a shopping row is in Today at all. Patched in place on every list
   * change; the glyph is swapped only when the kind moved, the way the rail
   * swaps its entry's, so a rename touches no node the focus could be on.
   */
  function paintGroup(group: GroupNode, list: List): void {
    group.name.textContent = list.name;
    group.link.setAttribute("href", listPath(list.id));
    group.link.setAttribute("aria-label", groupLabel(list));

    if (group.kind !== list.kind) {
      const glyph = icon(KIND_ICON[list.kind]);
      group.glyph.replaceWith(glyph);
      group.glyph = glyph;
      group.kind = list.kind;
    }

    if (list.kind === "todo") {
      group.word?.remove();
      group.word = undefined;
    } else if (group.word) {
      group.word.textContent = KIND_LABEL[list.kind];
    } else {
      group.word = el("span", { class: "row-kind", text: KIND_LABEL[list.kind] });
      group.link.insertBefore(group.word, group.chevron);
    }
  }

  /** Creates the group if it is not drawn yet, in the lists' own order. */
  function groupFor(listId: string): GroupNode | undefined {
    const existing = groups.get(listId);
    if (existing) return existing;

    const order = store.lists();
    const list = order.find((candidate) => candidate.id === listId);
    if (!list) return undefined;

    const group = groupNode(list);
    paintGroup(group, list);
    groups.set(listId, group);

    const at = order.findIndex((candidate) => candidate.id === listId);
    const next = order.slice(at + 1).find((candidate) => groups.has(candidate.id));
    groupsNode.insertBefore(group.node, next ? (groups.get(next.id)?.node ?? null) : null);
    return group;
  }

  function dropGroup(listId: string): void {
    const group = groups.get(listId);
    if (!group || group.rows.children.length > 0) return;
    group.node.remove();
    groups.delete(listId);
  }

  /**
   * Puts a row where the view's order says, among the rows already drawn in
   * its group.
   *
   * Against the rendered rows rather than a fresh read, for the reason the list
   * screen does it: a row the finger is on keeps its place in the walk.
   */
  function place(item: Item, row: Row): void {
    const group = groupFor(item.listId);
    if (!group) return;
    moveKeepingFocus(group.rows, row.node, slot(group, item));
    nodes.set(item.id, row);
  }

  /** The drawn row a row showing `item` goes before, or null for the end. */
  function slot(group: GroupNode, item: Item): Element | null {
    for (const drawn of group.rows.children) {
      const at = drawn instanceof HTMLElement ? drawn.dataset["id"] : undefined;
      const other = at === undefined ? undefined : nodes.get(at)?.shown();
      if (other !== undefined && order(other, item) > 0) return drawn;
    }
    return null;
  }

  /** "2 of 5", among the rows drawn in the row's group; kept drafts are not counted. */
  function positionOf(row: HTMLElement): string {
    const drawn = [...(row.parentElement?.children ?? [])].filter(
      (node) => node instanceof HTMLElement && node.dataset["id"] !== undefined,
    );
    const at = drawn.indexOf(row);
    return at < 0 ? "" : `${at + 1} of ${drawn.length}`;
  }

  /**
   * The rows either side of `row` in document order, across the groups: a
   * row last in its group has a neighbour in the next, and the focus goes
   * there rather than to the heading.
   */
  function neighboursOf(row: HTMLElement): { next?: HTMLElement; previous?: HTMLElement } {
    // A row kept for its draft is no landing: its controls are withdrawn.
    const all = [...groupsNode.querySelectorAll<HTMLElement>(".items .item")].filter((node) => node === row || !node.classList.contains("gone"));
    const at = all.indexOf(row);
    return { next: all[at + 1], previous: at > 0 ? all[at - 1] : undefined };
  }

  /**
   * Rows kept only for a draft whose item was deleted elsewhere, as on the
   * list screen (g54). Out of `nodes`, so nothing that walks the projection
   * finds them; still a draft, so leaving asks while one is up. The group
   * stays while one sits in it, and goes with it when it is dismissed. A
   * rebuild puts them back (`build`); their list going takes them.
   */
  const orphans = new Map<HTMLElement, Row>();
  function keepOrphan(id: string, row: Row): void {
    const listId = row.shown().listId;
    const anchor = menu.anchor();
    if (anchor !== undefined && row.node.contains(anchor)) menu.close();
    nodes.delete(id);
    orphans.set(row.node, row);
    row.orphan(() => {
      const { next, previous } = neighboursOf(row.node);
      const landing = (next ?? previous)?.querySelector<HTMLElement>(".check input");
      orphans.delete(row.node);
      row.node.remove();
      dropGroup(listId);
      paintState();
      (landing?.isConnected ? landing : focusableHeading()).focus({ preventScroll: true });
    });
    // After the row has withdrawn its ellipsis, where a closed menu left it.
    keepFocusIn(row.node);
  }

  function drop(id: string): void {
    const row = nodes.get(id);
    if (!row) return;
    const listId = row.shown().listId;
    // The menu is open on this row: closed, and the focus put where a
    // deleted row's goes rather than left on a node that is gone. Here and
    // not only on a delete, because the reconciler drops rows too -- a date
    // or a list moving one out of the projection -- and a menu left open over
    // a detached row would re-place itself against nothing.
    const anchor = menu.anchor();
    const underMenu = anchor !== undefined && row.node.contains(anchor);
    // Or the focus is in the row itself -- on the ellipsis the previous
    // drop landed it on, when two neighbours go in one delta: it moves on
    // again rather than falling to the document with the row.
    const holding = row.node.contains(document.activeElement);
    const around = underMenu || holding ? neighboursOf(row.node) : undefined;
    const landing = (around?.next ?? around?.previous)?.querySelector<HTMLElement>(".item-actions");
    row.node.remove();
    nodes.delete(id);
    dropGroup(listId);
    if (underMenu) menu.close();
    if (underMenu || holding) (landing?.isConnected ? landing : focusableHeading()).focus({ preventScroll: true });
  }

  /**
   * Moves a row that is already drawn to where the view's order now puts it,
   * keeping the node it is drawn in.
   *
   * Not `drop` plus a fresh `itemRow`: the node may be holding an inline edit
   * nobody has committed, and a rebuilt row throws it away. The list screen has
   * had this rule since its own `relocate`; a reorder arriving from another
   * device would otherwise cost a draft here and not there. The panel beside it
   * is left alone for the same reason, and does not need rebinding — it watches
   * its own position and repaints its move controls itself.
   */
  function relocate(item: Item, row: Row): void {
    // Updated first: `place` walks the drawn rows comparing what each one is
    // showing, so this row has to be showing its new key and date before it
    // is asked where they belong.
    row.update(item);
    // Moved in place, as the list screen's own `relocate` does (g54), unless
    // it has no group to go to.
    if (!groupFor(item.listId)) row.node.remove();
    place(item, row);
  }

  /**
   * `relocate`, for a row staying in its group, noting the move when a new
   * date or time is what caused it (g91). A reorder is not noted: whoever
   * made it has said so already, and the list screen is where it is made.
   */
  function reposition(before: Item, next: Item, row: Row): void {
    const was = positionOf(row.node);
    relocate(next, row);
    const rescheduled = (before.due?.date ?? null) !== (next.due?.date ?? null) ||
      (before.due?.time ?? null) !== (next.due?.time ?? null);
    const now = positionOf(row.node);
    if (!rescheduled || now === "" || now === was) return;
    const name = store.list(next.listId)?.name;
    movedNote = `Moved ${next.text} to ${now}${name === undefined ? "" : ` in ${name}`}.`;
  }

  // ---- what is on screen --------------------------------------------------

  function emptyContent(): Node[] {
    if (store.status() === "loading") {
      return [el("p", { class: "muted", text: "Loading…" })];
    }
    if (store.status() === "unreachable") return [unreachable(store)];

    if (store.lists().length === 0) {
      return [
        el("div", {
          class: "empty",
          children: [
            el("p", { class: "muted", text: "You have no lists yet." }),
            // A link, not a New list of its own: every screen has exactly one
            // plus, and the index's opens the popup. Capture needs a list and
            // there is no Inbox here, so this points at where lists are made.
            el("a", { text: "Go to your lists", attrs: { href: "/", "data-link": true } }),
          ],
        }),
      ];
    }

    return [
      el("div", {
        class: "empty",
        children: [
          el("p", { class: "muted", text: NOTHING[which] }),
          el("a", {
            class: "link",
            text: "Go to your lists",
            attrs: { href: "/", "data-link": true },
          }),
        ],
      }),
    ];
  }

  /**
   * What the screen says when it has no rows to show — and, when that changes,
   * says out loud.
   *
   * This slot turns over under the person: loading becomes unreachable when the
   * first sync gives up, and either becomes a list of rows when one lands.
   * Those are the transitions somebody waiting on a blank screen most needs to
   * hear, and the slot itself cannot carry a live region — it is hidden while
   * empty, which is exactly when a region would have to be in the accessibility
   * tree. So it goes through the shell's, like every other message.
   *
   * Guarded on the text, because this repaints on every store change and a
   * region that repeats itself every few seconds is worse than a silent one.
   */
  function paintState(): void {
    // C05: everything due is done and hidden is not an empty day. The header
    // keeps the Show action, so no second button here and no invitation.
    const allDone = groups.size === 0 && completed.hidden && doneCount() > 0;
    state.replaceChildren(
      ...(allDone
        ? [el("p", { class: "muted agenda-all-done", text: allDoneMessage(doneCount()) })]
        : groups.size === 0
          ? emptyContent()
          : []),
    );
    const saying = state.textContent?.trim() ?? "";
    if (saying !== said) {
      said = saying;
      if (saying !== "") shell.announce(saying);
    }
  }

  function paintMore(): void {
    if (hidden.size === 0) return moreSlot.replaceChildren();

    moreSlot.replaceChildren(
      el("button", {
        // Its own class and not `secondary` (A04): full width after the last
        // group, and quieter than an action -- the stylesheet says how.
        class: "agenda-show-more",
        // The exact number, so un-bounding the page is something the person
        // asked for knowing its size rather than something the app did.
        text: `Show ${hidden.size.toLocaleString()} more`,
        attrs: { type: "button" },
        on: {
          click: () => {
            // An explicit action, so a valid draft is committed rather than
            // dropped — and a refused write stops the expansion rather than
            // costing the draft.
            if (!editors.resolve()) return;
            expanded = true;
            build();
          },
        },
      }),
    );
  }

  /**
   * Everything, from scratch, against today's boundary.
   *
   * Called on mount and when the app comes back to the foreground — the two
   * moments the answer can have changed without anything arriving. No timer:
   * the sync engine deliberately has none either, and a projection that
   * rewrote itself on a schedule would move rows under an idle finger.
   */
  /**
   * A rebuild replaces every row, and a draft lives in the DOM — so every
   * caller of `build` resolves the open editor first (`editors.resolve()`) or
   * defers to it (`editors.editing()`). The "Show N more" control rebuilds
   * too, and clicking it while editing would otherwise throw the draft away
   * just as silently as a focus rebuild would.
   */
  function build(): void {
    today = todayIso();

    // The filter is inside the predicate, so a hidden completed row never
    // takes a place in the budget (C05): what Show more counts is what
    // showing would draw.
    const { groups: built, hidden: left } = project(
      store.lists(),
      store.allItems(),
      (item) => member(item, today) && !filtered(item),
      expanded ? Number.POSITIVE_INFINITY : BUDGET,
      order,
    );

    // A rebuild replaces every row, the one the menu is open beside
    // included: closed first, and the focus it hands back to a button about
    // to be replaced is moved on to the row's new ellipsis below.
    const menuRow = menu.anchor()?.closest<HTMLElement>(".item")?.dataset["id"];
    menu.close();

    groups.clear();
    nodes.clear();
    hidden = new Set(left);
    // A kept draft's field, if it has the focus, gets it back once the row
    // is put back below.
    const keptFocus = [...orphans.keys()].find((node) => node.contains(document.activeElement)) ? document.activeElement as HTMLElement : undefined;

    groupsNode.replaceChildren(
      ...built.map((group) => {
        const drawn = groupNode(group.list);
        paintGroup(drawn, group.list);
        groups.set(group.list.id, drawn);
        for (const item of group.items) {
          const row = itemRow(item);
          nodes.set(item.id, row);
          drawn.rows.append(row.node);
        }
        return drawn.node;
      }),
    );

    // A draft kept for copying outlives the rebuild (g54): back among its
    // list's rows where the view's order puts it, the group drawn for it if
    // the projection no longer has one.
    for (const [node, row] of orphans) {
      const group = groupFor(row.shown().listId);
      if (group) group.rows.insertBefore(node, slot(group, row.shown()));
      else orphans.delete(node);
    }
    if (keptFocus?.isConnected) keptFocus.focus({ preventScroll: true });

    paintState();
    paintMore();
    paintControl();

    if (menuRow !== undefined) {
      const again = nodes.get(menuRow)?.node.querySelector<HTMLElement>(".item-actions");
      (again ?? focusableHeading()).focus({ preventScroll: true });
    }
  }

  // ---- selection ----------------------------------------------------------

  function selectItem(id: string): void {
    // Another row over a changed draft is asked about first (E05); the same
    // row again is not a move.
    if (shownId !== undefined && id !== shownId) {
      shell.detail.guarded(() => go(id));
      return;
    }
    go(id);
  }

  function go(id: string): void {
    const replace = shell.detail.persistent() || selectedId !== undefined;
    navigate(agendaItemPath(which, id), { replace });
    if (!replace) pushed = true;
  }

  let closing = false;
  function dismiss(): void {
    closing = true;
    if (pushed) {
      pushed = false;
      back();
      return;
    }
    // Back to the projection, never into the list the row happens to live in:
    // closing a panel is not a request to go somewhere else.
    navigate(agendaPath(which), { replace: true, local: true });
  }

  function markSelected(id: string): void {
    nodes.get(id)?.node.classList.toggle("selected", id === selectedId);
  }

  /** Draws a row the budget hid, for a route that names it. */
  function drawHidden(item: Item): void {
    if (!hidden.has(item.id) || nodes.has(item.id)) return;
    place(item, itemRow(item));
    // Only once it is really drawn: `place` declines when the list is not in
    // `store.lists()`, and forgetting it was hidden anyway would put the
    // count beside the control out by one.
    if (nodes.has(item.id)) {
      hidden.delete(item.id);
      paintState();
      paintMore();
    }
  }

  /** Back from a note's page (g77): the focus on the row's ellipsis, drawn if the budget hid it. */
  function returnTo(itemId: string): void {
    const item = store.item(itemId);
    if (item && store.list(item.listId)) drawHidden(item);
    nodes.get(itemId)?.node.querySelector<HTMLElement>(".item-actions")?.focus();
  }

  function select(itemId: string | undefined): void {
    closing = false;
    const previous = selectedId;
    selectedId = itemId;
    if (previous !== undefined && previous !== itemId) markSelected(previous);

    if (itemId === undefined) {
      pushed = false;
      shownId = undefined;
      shell.detail.clear();
      // The row the panel showed stayed while it was up, whatever the filter
      // said; `clear()` has handed the focus back to its Details, and now it
      // leaves with the focus moving on.
      sweep();
      return;
    }

    // Already up. The panel is a form somebody may be halfway through, and a
    // route that did not actually move must not throw that away.
    if (itemId === shownId) return;

    const item = store.item(itemId);
    const list = item ? store.list(item.listId) : undefined;

    // Behind the "Show N more" control. A row somebody navigated to directly is
    // not the leak the budget guards against — a change arriving for a row
    // nobody asked about is — so the link is honoured rather than bounced, and
    // the panel gets the return-focus target it would otherwise go without.
    if (item && list) drawHidden(item);

    // A link to a completed member the filter is hiding (C05): drawn for this
    // visit, with a line saying why, and the preference left as it was.
    if (item && list && completed.hidden && isDone(item) && member(item, today) && !nodes.has(itemId)) {
      revealed = itemId;
      place(item, itemRow(item));
      if (nodes.has(itemId)) {
        hidden.delete(itemId);
        notice.replaceChildren(el("p", { class: "muted", text: revealedMessage(item.text) }));
        shell.announce(revealedMessage(item.text));
        paintState();
        paintMore();
      } else {
        revealed = undefined;
      }
    }

    // A row that is drawn counts even when it is no longer a member: ticking
    // something off leaves it on screen for the rest of the visit, and its
    // panel has to keep opening while it is there.
    const here = item !== undefined && (nodes.has(itemId) || member(item, today));

    if (!item || !list || !here) {
      // Not downloaded yet is not the same as gone: the first sync applies
      // every list change before any item change, so a fresh device reaches
      // here with the row still missing.
      if ((!item || !list) && store.status() !== "ready") return;

      // A stale bookmark — the date moved, or the day did. A panel over a
      // projection with no matching row has nowhere to return focus to.
      selectedId = undefined;
      shownId = undefined;
      shell.detail.clear();
      navigate(agendaPath(which), { replace: true });
      return;
    }

    shownId = itemId;
    markSelected(itemId);
    const actions = nodes.get(itemId)?.node.querySelector<HTMLElement>(".item-actions") ?? undefined;
    // The fields and nothing else (g18): Open list and Delete are the row's
    // menu's, reached from the ellipsis without a form.
    const editor = editorPanel(shell, item, list, { onDismiss: dismiss, onResult });
    shell.detail.present(editor.node, {
      label: `Edit ${item.text}`,
      title: "Edit item",
      onDismiss: dismiss,
      guard: editor.guard,
      // The row's ellipsis, whose menu's Edit opened the panel — the row
      // body is not a control — read when the panel comes down, because the
      // row may have gone by then: deleted from its menu, or re-dated off
      // the projection. Then the neighbour captured before it went, and
      // failing that the heading, which a rebuild leaves as the one thing
      // still standing where the row was.
      // The row as drawn *now*, first: a rebuild between the panel opening
      // and closing — the filter flipping under it — replaces every row node,
      // and the button captured above is then a detached one.
      returnFocus: () =>
        nodes.get(itemId)?.node.querySelector<HTMLElement>(".item-actions") ??
        (actions?.isConnected ? actions : (lostFocus ?? focusableHeading())),
    });
  }

  // Where the focus goes when the selected row is taken off the projection
  // under its panel: the next row's Details in its group, else the previous
  // row's. Captured just before the row is dropped (D09); `dropGroup` may
  // take the group's heading with it, which is why that is not a candidate.
  let lostFocus: HTMLElement | undefined;

  function noteLoss(id: string): void {
    if (selectedId !== id) return;
    const row = nodes.get(id)?.node;
    const neighbour = row?.nextElementSibling ?? row?.previousElementSibling;
    lostFocus = neighbour?.querySelector<HTMLElement>(".item-actions") ?? undefined;
  }

  /** The heading is not a tab stop, but it can be given the focus once. */
  function focusableHeading(): HTMLElement {
    if (!heading.hasAttribute("tabindex")) heading.setAttribute("tabindex", "-1");
    return heading;
  }

  /**
   * The selection, checked against what is actually drawn.
   *
   * Four paths change the row set — a delete, a re-date, a move out, a rebuild
   * — and none of them can tell on its own whether the row under the panel
   * survived. A panel whose `returnFocus` node has been removed has nowhere to
   * put focus back, and its route still names an item that is not on screen.
   */
  function reconcile(): void {
    // A selection with no panel is a deep link still waiting for its first
    // sync — `select` returns early while the store is not ready, leaving the
    // id set and nothing shown. Dismissing that would bounce the link on the
    // first unrelated change to arrive, which on a fresh device is every time.
    if (selectedId === undefined || shownId === undefined) return;
    if (nodes.has(selectedId)) markSelected(selectedId);
    // Their own Delete asks nothing: the question already named the draft it
    // discards (D09). Otherwise through the guard (E05): a row re-dated,
    // moved or deleted on another device must not take a changed draft with
    // it. Keep editing leaves the panel up over a projection that no longer
    // shows the row, and Save still reaches the item by id.
    else if (deleting === selectedId) dismiss();
    else shell.detail.guarded(dismiss);
  }

  /**
   * Delete, from the row's menu (g18): the question stands alone in the popup
   * host — see the list screen's `askDelete`, which this mirrors. Where the
   * focus lands after: the row's ellipsis when it survived, else the next
   * row's in its group, else the previous row's, else the heading.
   */
  function askDelete(id: string): void {
    const target = store.item(id);
    if (!target) return;
    // The inspector's draft on this row, or the row's own editor's: either
    // goes with the row, and the question says so.
    const dirty = (shownId === id && shell.detail.dirty()) || (nodes.get(id)?.dirty() ?? false);
    const row = nodes.get(id)?.node;
    const ellipsis = row?.querySelector<HTMLElement>(".item-actions");
    const around = row ? neighboursOf(row) : undefined;
    const next = around?.next?.querySelector<HTMLElement>(".item-actions");
    const previous = around?.previous?.querySelector<HTMLElement>(".item-actions");
    const question = shell.confirm(
      {
        title: deleteTitle(target.text),
        message: dirty ? `${DELETE_ITEM.message} ${DELETE_ITEM.discards}` : DELETE_ITEM.message,
        keep: {
          label: "Cancel",
          onChoose: () => {
            asked = undefined;
          },
        },
        act: {
          label: "Delete item",
          danger: true,
          onChoose: () => {
            // Let go before the write, whose change event arrives
            // synchronously: this delete is not another device's.
            asked = undefined;
            removeNow(id);
          },
        },
      },
      {
        returnFocusTo: () =>
          [ellipsis, next, previous].find((candidate) => candidate?.isConnected) ??
          focusableHeading(),
      },
    );
    asked = { id, listId: target.listId, close: question.close };
  }

  /** The standalone Delete question while it stands, and the row it names. */
  let asked: { id: string; listId: string; close(): void } | undefined;

  function removeNow(id: string): void {
    deleting = id;
    try {
      store.removeItem(id);
      setStatus(undefined);
    } catch (error: unknown) {
      fail(error);
    } finally {
      deleting = undefined;
    }
  }

  /**
   * Re-opens the panel against a fresh `Item` and `List`.
   *
   * `editorPanel` takes a snapshot of both and asks the list which fields to
   * offer, so anything that changes either has to redraw it — and only then,
   * since a redraw costs whatever was typed. `shownId` is cleared first because
   * `select` returns early when the route has not moved, which is right on every
   * other path and is exactly the path a rebind arrives on. `list.ts` learned
   * this the same way; see the note above its own kind change.
   */
  function rebind(): void {
    if (selectedId === undefined) return;
    shownId = undefined;
    select(selectedId);
  }

  // ---- what the store tells this screen -----------------------------------

  function item(change: ItemChange): void {
    movedNote = undefined;
    const existing = nodes.get(change.id);

    if (!change.item) {
      hidden.delete(change.id);
      if (revealed === change.id) clearNotice();
      noteLoss(change.id);
      // A menu open on the row that is going is `drop`'s to close. Another
      // device's delete under a typed-into editor keeps the draft (g54).
      if (existing?.dirty() && deleting !== change.id) keepOrphan(change.id, existing);
      else if (existing) drop(change.id);
      // The Delete question was about this row and another device got
      // there first: closed, rather than left naming a row that is gone or
      // stacked over the draft question the reconciler may ask. After the
      // drop, so that its return focus lands on the neighbour.
      if (asked?.id === change.id) {
        const { close } = asked;
        asked = undefined;
        close();
      }
      paintState();
      paintMore();
      // The count beside the control covers rows the filter hides, and a
      // hidden one going changes it with no row to drop.
      paintControl();
      reconcile();
      return;
    }

    const next = change.item;
    const belongs = member(next, today);
    // A revealed row that is not done any more is simply a row, and one that
    // has left the projection is not here to explain.
    if (revealed === change.id && (!isDone(next) || !belongs)) clearNotice();

    if (existing) {
      const before = existing.shown();
      const moved = before.listId !== next.listId;
      // A due date is the one thing that may take a row off a projection while
      // it is being looked at, and the design system says so in as many words.
      // A tick may not: the row stays, and is simply absent the next time the
      // projection is built — unless completed rows are hidden, which is C04's
      // explicit exception (A03): then a ticked row leaves now.
      const redated = (before.due?.date ?? null) !== (next.due?.date ?? null);
      // The same row, so anything but a tie means a field the view orders by
      // has changed: the key in Today, and in Upcoming the date or the time
      // as well (g91).
      const reordered = order(before, next) !== 0;

      if (filtered(next)) {
        dropRows([change.id]);
        // Said for this device's own tick, and only when there is still a
        // projection to say it over: an emptied one says "All done" from
        // `paintState`, and that line carries the count.
        if (ticking === change.id && groups.size > 0) {
          shell.announce(tickedHiddenMessage(next.text, doneCount()));
        }
      } else if (!belongs && (moved || redated)) {
        noteLoss(change.id);
        drop(change.id);
      } else if (completed.hidden && isDone(next) && shownId === change.id && !closing) {
        // The row the panel shows was ticked with the filter on: it stays
        // while the panel is up, and the panel comes down — after the draft
        // question when it holds one — with the row leaving in the sweep.
        // Until then it sits where its date now puts it, like any other row.
        if (reordered) reposition(before, next, existing);
        else existing.update(next);
        if (ticking === change.id) shell.announce(tickedHiddenMessage(next.text, doneCount()));
        shell.detail.guarded(dismiss);
      } else if (moved) {
        // Into another list's group, and the same node: an open draft goes
        // with it, as it does through a reorder (g54). The old group goes if
        // that left it empty.
        relocate(next, existing);
        dropGroup(before.listId);
        if (menu.isOpen()) menu.refresh();
        // The row belongs to another list now, and the open panel is still
        // offering the fields the old one asked for.
        if (selectedId === change.id) rebind();
      } else if (reordered) {
        // A reorder, which the Details panel now offers from here too. The row
        // keeps its group — a move is within one list — but its place inside
        // that group has changed, and a projection that showed a stale order
        // would be contradicting the list screen it is a view of.
        //
        // In Upcoming a new date or time does the same (g91): the row moves
        // to its new place as the edit is saved, in the node it was drawn in,
        // so a draft in it and the focus stay where they were.
        //
        // Reorders from another device arrive here too — see the same handler
        // in `views/list.ts`.
        //
        // Not the same as the settled rule that there is no reorder *control*
        // on these rows: an anchor named by neighbours from different lists
        // still means nothing, and this is a move the item's own list decided.
        reposition(before, next, existing);
      } else {
        existing.update(next);
      }
    } else if (hidden.has(change.id)) {
      // Behind the control, and it stays there: letting a change pull one
      // through would make the budget leak a row at a time. A row the filter
      // would not draw stops being counted, or the control would offer rows
      // that showing more would not draw.
      if (!belongs || filtered(next)) hidden.delete(change.id);
    } else if (belongs && !filtered(next)) {
      // Something genuinely new — created here, or arrived in a delta. It is
      // drawn even past the budget, because a row that appeared and then hid
      // itself reads as a sync fault.
      place(next, itemRow(next));
    }

    paintState();
    paintMore();
    paintControl();
    reconcile();
    // Re-placed against a row that may have moved; the entries here do not
    // depend on the list's order.
    if (menu.isOpen()) menu.refresh();
  }

  function list(change: ListChange): void {
    const group = groups.get(change.id);

    if (!change.list) {
      // The items go too, each as its own change; this is what takes the
      // heading with them rather than leaving an empty section behind.
      if (group) {
        // A menu open on one of its rows: closed while its anchor is still
        // in the document, and the focus put on the heading — the tombstones
        // that follow find no row to `drop`, so this is the one place that
        // sees the row go.
        const anchor = menu.anchor();
        if (anchor && group.node.contains(anchor)) {
          menu.close();
          focusableHeading().focus({ preventScroll: true });
        }
        for (const drawn of [...group.rows.children]) {
          const id = drawn instanceof HTMLElement ? drawn.dataset["id"] : undefined;
          if (id) nodes.delete(id);
          // A kept draft goes with its list, as the list screen's does.
          else if (drawn instanceof HTMLElement) orphans.delete(drawn);
        }
        group.node.remove();
        groups.delete(change.id);
      }
      // Likewise the Delete question about one of its rows: the tombstones
      // that follow find no row here, so this is where it is closed -- after
      // the group has gone, so its return focus falls through the row and
      // its neighbours to the heading. By the list the question was asked
      // about, not the group as drawn: a re-date may have taken the row, and
      // with it the group, out of the projection first.
      if (asked?.listId === change.id) {
        const { close } = asked;
        asked = undefined;
        close();
      }
      paintState();
      return;
    }

    if (!group) return;

    const rekinded = group.kind !== change.list.kind;
    paintGroup(group, change.list);
    // A Shopping row leads with its quantity and no other kind's does (g58),
    // so the group's rows repaint. An open row editor keeps its draft: it is
    // in the field, which a repaint does not write.
    if (rekinded) {
      for (const [id, row] of nodes) {
        if (row.shown().listId === change.id) row.update(store.item(id) ?? row.shown());
      }
    }
    const order = store.lists();
    const at = order.findIndex(candidate => candidate.id === change.id);
    const next = order.slice(at + 1).find(candidate => groups.has(candidate.id));
    const before = next ? groups.get(next.id)!.node : null;
    if (group.node.nextElementSibling !== before) {
      const focused = group.node.contains(document.activeElement) ? document.activeElement as HTMLElement : undefined;
      groupsNode.insertBefore(group.node, before);
      focused?.focus({ preventScroll: true });
      if (menu.isOpen()) menu.refresh();
    }

    // A shopping list stops offering a due date and starts offering a quantity,
    // and a panel open over one of its rows is showing the wrong form until it
    // is reopened. Only on a real change: a rename must not cost a draft.
    if (rekinded && selectedId !== undefined && store.item(selectedId)?.listId === change.id) {
      rebind();
    }
  }

  // ---- assembly -----------------------------------------------------------

  build();

  const stopItems = store.onAllItems(item);
  const stopLists = store.onLists(list);
  // The mirror settling changes what an empty screen means, and it is also
  // when a deep link stops being early and starts being wrong.
  const stopStatus = store.onStatus(() => {
    paintState();
    if (selectedId !== undefined) select(selectedId);
  });

  const refresh = () => {
    if (document.visibilityState === "hidden") return;
    // Coming back to the app is not an explicit action on this row, so an open
    // editor defers the rebuild rather than being committed by it: switching to
    // another app and back must not end an edit. The boundary can wait — they
    // are looking at the row, not at midnight.
    // A kept draft likewise: the rebuild would keep it, but not its focus.
    if (editors.editing() || orphans.size > 0) return;
    build();
    // The one moment a selected row can leave with nothing having arrived:
    // midnight moves the boundary, and this is where the rows a tick left
    // behind are swept.
    reconcile();
  };
  window.addEventListener("focus", refresh);
  document.addEventListener("visibilitychange", refresh);

  return {
    node,

    update(route: Route) {
      if (route.name !== which) return;
      select(route.itemId);
    },

    returnTo,

    dirty: () => [...nodes.values()].some((row) => row.dirty()) || orphans.size > 0,

    dispose() {
      menu.close();
      // The Delete question belongs to the popup host, which outlives this
      // screen: left up, nothing would ever close it, since the changes
      // that would are unsubscribed below.
      if (asked) {
        const { close } = asked;
        asked = undefined;
        close();
      }
      stopItems();
      stopLists();
      stopStatus();
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", refresh);
      // The panel belongs to the shell, which outlives this screen.
      shell.detail.clear();
    },
  };
}
